import { useEffect } from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import {
	getCompanyThemingSettings,
	GatewayError,
} from '../../clients/gateway-client/gatewayClient';
import { getCompanyThemingSettingsFromAdminSettings } from '../../clients/gateway-client/adminSettingsMappers';
import { useAdminSettingsSelector } from '../../clients/settings-client/adminSettingsQuery';
import { CompanyThemingSettingsData } from '../../clients/gateway-client/types';
import {
	exposeUserToSettingsDataMigrationExperiment,
	isSiteSettingsReadFromAdminSettings,
} from '../../experiment/experiments';
import { logError } from '../../utils/logger';
import { useSite } from '../useSite';
import { postMessageToParent } from '../../message-bus/postMessageToParent';

const THIRTY_MINUTES_MS = 30 * 60 * 1000;

/**
 * Checks chrome.storage.local for cached company theming settings via message bus
 */
function getCachedThemingSettings(siteId: string): Promise<CompanyThemingSettingsData | null> {
	return new Promise((resolve) => {
		const timeoutId = setTimeout(() => {
			window.removeEventListener('message', messageHandler);
			resolve(null);
		}, 2000);

		const messageHandler = (event: MessageEvent): void => {
			if (event.data.type === 'GET_COMPANY_THEMING_CACHE_RESPONSE') {
				clearTimeout(timeoutId);
				window.removeEventListener('message', messageHandler);
				resolve(event.data.data);
			}
		};

		window.addEventListener('message', messageHandler, false);
		postMessageToParent({ type: 'GET_COMPANY_THEMING_CACHE', siteId });
	});
}

export function setCachedThemingSettings(
	siteId: string,
	themingSettings: CompanyThemingSettingsData,
): void {
	postMessageToParent({
		type: 'SET_COMPANY_THEMING_CACHE',
		siteId,
		themingSettings,
	});
}

/**
 * Fetches company theming settings with chrome.storage.local caching
 * - First checks cache, returns if fresh (< 30 min old)
 * - If stale/missing, fetches from API and updates cache
 */
async function fetchThemingSettingsWithCache(siteId: string): Promise<CompanyThemingSettingsData> {
	const cachedData = await getCachedThemingSettings(siteId);

	if (cachedData !== null) {
		return cachedData;
	}

	const freshData = await getCompanyThemingSettings(siteId);
	setCachedThemingSettings(siteId, freshData);

	return freshData;
}

const onThemingError = (error: GatewayError): void => {
	logError({ error, impact: 'Error fetching company theming settings' });
};

export function useCompanyThemingSettings(): UseQueryResult<
	CompanyThemingSettingsData,
	GatewayError
> {
	const siteContext = useSite({ allowUndefined: true });

	const siteId = siteContext?.site.workspace.cloudId;
	const readFromAdminSettings = isSiteSettingsReadFromAdminSettings();

	useEffect(() => {
		exposeUserToSettingsDataMigrationExperiment();
	}, []);

	/*
	 * Both queries are always declared to keep the hook order stable, but only
	 * the cohort's query is enabled. On the admin-settings cohort theming is a
	 * projection of the shared admin-settings query, so it adds no round trip.
	 */
	const adminSettingsQuery = useAdminSettingsSelector({
		siteId,
		select: getCompanyThemingSettingsFromAdminSettings,
		enabled: readFromAdminSettings,
		onError: onThemingError,
	});

	const legacyQuery = useQuery<CompanyThemingSettingsData, GatewayError>({
		queryKey: ['companyThemingSettings', siteId],
		queryFn: () => fetchThemingSettingsWithCache(siteId as string),
		enabled: !!siteId && !readFromAdminSettings,
		throwOnError(error) {
			onThemingError(error);
			return false;
		},
		staleTime: THIRTY_MINUTES_MS,
		gcTime: THIRTY_MINUTES_MS * 2,
		refetchOnWindowFocus: false,
		retry: (failureCount, error) => {
			if (error.statusCode === 429) {
				return false;
			}
			return failureCount < 2;
		},
	});

	return readFromAdminSettings ? adminSettingsQuery : legacyQuery;
}
